import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Grid,
  Group,
  List,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  Popover,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { RangeCalendar } from '@mantine/dates';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconCalendarDue,
  IconCirclePlus,
  IconClipboardData,
  IconDotsVertical,
  IconEdit,
  IconFile,
  IconFileImport,
  IconFileInfo,
  IconFingerprint,
  IconShield,
  IconTrash,
  IconUserCheck,
  IconUserOff,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useEffect, useRef, useState } from 'react';
import {
  BatchImportPreview,
  BatchImportResult,
  PayslipPending,
} from '../../@types/employee-payslip';
import { CardFile, CardPayslipFile } from '../../components/custom-card-file';
import { CustomDateRangePicker } from '../../components/custom-date-range-picker';
import { CustomLoader } from '../../components/custom-loader';
import { CustomMultipleFileButton } from '../../components/custom-multiple-file-input';
import { rootNavigate } from '../../components/custom-router';
import { Page } from '../../components/page';
import { PdfPageCanvas } from '../../components/pdf-page-canvas';
import {
  createBiometric,
  createBiometricService,
  findBiometricByUser,
} from '../../data/biometric';
import { getCompaniesMinRequest } from '../../data/company';
import { searchPayslipEmployeesRequest } from '../../data/employee-payslip';
import { updateEmployeeRequest } from '../../data/employees';
import {
  exportEpiHistoryReceiptRequest,
  exportEpiReceiptRequest,
} from '../../data/exportables';
import { useDeleteUserBiometric } from '../../data/hooks/biometric';
import { useGenerateUserMirror } from '../../data/hooks/daily-work';
import {
  useAddEmployeeFiles,
  useGetEmployeeFiles,
  useListEpiDeliveries,
  useListEpis,
  useMarkEpiAsDelivered,
  useUnlinkEpi,
} from '../../data/hooks/employee';
import {
  useCreateEmployeePaySlip,
  useGetPayslipPending,
  useImportBatchPayslip,
  useListEmployeePayslipByUser,
  usePreviewBatchPayslip,
} from '../../data/hooks/employee-payslip';
import { useUploadMultipleFiles } from '../../data/hooks/files';
import {
  useGetUsersPaginated,
  useRemoveManyUsers,
  useRestoreUser,
} from '../../data/hooks/user';
import { useGetWorkScaleList } from '../../data/hooks/work-scale';
import { removeUserRequest, updateUserRequest } from '../../data/user';
import { useAuth } from '../../hooks/useAuth';
import { Biometric } from '../../models/biometric';
import { CompanyMin } from '../../models/company';
import { EpiDeliveryReasonOptions } from '../../models/employees';
import { User, UserRole, UserType } from '../../models/user';
import { dayjsPlugins, formatLocale } from '../../providers/dayjs-plugins';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { userRoleHumanized, userRoleList } from '../../utils/constants/user';
import { AppRoutePaths } from '../../utils/enums/routes';
import { formatBRL } from '../../utils/helpers';

type PageModalState =
  | 'user-mirror'
  | 'confirm-mirror'
  | 'biometric'
  | 'work-scale'
  | 'files'
  | 'delete-biometric'
  | 'dismiss'
  | 'restore'
  | 'link-epi'
  | 'confirm-register-time'
  | 'import-payslip'
  | 'import-payslip-preview'
  | 'import-payslip-result'
  | null;

function downloadFile(response: Blob, fileName: string) {
  const url = URL.createObjectURL(new Blob([response]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(url);
}

const CALENDAR_INITIAL_STATE: [Date | null, Date | null] = [null, null];

export function UserList() {
  const { user } = useAuth();
  const [pageLoading, setPageLoading] = useState(false);
  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [pendingRegisterTimeChange, setPendingRegisterTimeChange] = useState<{
    id: number;
    value: boolean;
  } | null>(null);
  const [registerTimeBlockMessage, setRegisterTimeBlockMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState<[Date | null, Date | null]>(
    CALENDAR_INITIAL_STATE,
  );
  const [selectedUser, setSelectedUser] = useState<User[]>([]);
  const [payslipFilterIds, setPayslipFilterIds] = useState<Set<number> | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<string | null>('users');
  const [pendingData, setPendingData] = useState<PayslipPending | null>(null);
  const { fetch: getPendingFetcher, loading: pendingLoader } =
    useGetPayslipPending();
  const [epiUsersToPrint, setEpiUsersToPrint] = useState<number[]>([]);
  const [selectedUserId, setSelectUserId] = useState<number>();
  const [userBiometric, setUserBiometric] = useState<Biometric>();
  const [companyList, setCompanyList] = useState<CompanyMin[]>([]);
  const [addFileCollapse, setAddFileCollapse] = useState(false);
  const { fetch: restoreUserFetcher, loading: restoreUserLoader } =
    useRestoreUser();
  const { fetch: listEmployeeEpisFetcher, response: employeeEpis } =
    useListEpis();
  const { unlink: unlinkEpiFetcher, loading: unlinkEpisLoading } =
    useUnlinkEpi();
  const { deliver: markEpiAsDeliveredFetcher, loading: markEpiDeliverLoading } =
    useMarkEpiAsDelivered();
  const { fetch: listEpiDeliveriesFetcher, response: epiDeliveries } =
    useListEpiDeliveries();
  const [printEpiLoading, setPrintEpiLoading] = useState(false);
  const [deliverModalRecordId, setDeliverModalRecordId] = useState<
    number | null
  >(null);
  const deliverForm = useForm({
    initialValues: { reason: '', quantity: 1 },
  });
  const {
    fetch: getUsersPaginatedFetcher,
    loading: getUsersPaginatedLoader,
    response: getUsersPaginatedData,
  } = useGetUsersPaginated();
  const { fetch: generateMirrorFetcher, loading: generateMirrorLoader } =
    useGenerateUserMirror();
  const { fetch: removeManyUserFetcher, loading: removeManyUserLoader } =
    useRemoveManyUsers();
  const page = useRef(1);
  const pageLimit = useRef(10);

  const [createdAtRange, setCreatedAtRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const formFilter = useForm({
    initialValues: {
      username: '',
      role: '',
      company: '',
      inactive: false,
      workScale: '',
    },
  });

  const formAssignWorkScale = useForm({
    initialValues: {
      workScale: '',
    },
  });

  const formAddFile = useForm({
    initialValues: {
      files: [],
      type: '',
    },
  });

  const formDismiss = useForm({
    initialValues: {
      feedback: '',
      dismissReasons: [] as string[],
    },
  });

  const {
    fetch: getWorkScaleFetcher,
    response: getWorkScaleData,
    loading: getWorkScaleLoading,
  } = useGetWorkScaleList();

  const {
    fetch: getEmployeeFilesFetcher,
    response: getEmployeeFilesData,
    loading: getEmployeeFilesLoading,
  } = useGetEmployeeFiles();

  const { fetch: addEmployeeFilesFetcher, loading: addEmployeeFilesLoading } =
    useAddEmployeeFiles();

  const { fetch: uploadFilesFetcher, loading: uploadFilesLoader } =
    useUploadMultipleFiles();

  const {
    fetch: uploadEmployeePayslipFetcher,
    loading: uploadEmployeePayslipLoader,
  } = useCreateEmployeePaySlip();

  const {
    fetch: getEmployeePayslipFilesFetcher,
    response: getEmployeePayslipFilesData,
    loading: getEmployeePayslipFilesLoading,
  } = useListEmployeePayslipByUser();

  const {
    fetch: previewBatchPayslipFetcher,
    loading: previewBatchPayslipLoader,
  } = usePreviewBatchPayslip();
  const {
    fetch: importBatchPayslipFetcher,
    loading: importBatchPayslipLoader,
  } = useImportBatchPayslip();
  const [importPreviewFile, setImportPreviewFile] = useState<File | null>(null);
  const [importBatchPreview, setImportBatchPreview] =
    useState<BatchImportPreview | null>(null);
  const [importBatchResult, setImportBatchResult] =
    useState<BatchImportResult | null>(null);
  const [expandedPage, setExpandedPage] = useState<number | null>(null);
  const [importReferenceMonth, setImportReferenceMonth] = useState<
    string | null
  >(null);
  const [sendMirrorGlobal, setSendMirrorGlobal] = useState(false);
  const [mirrorExcluded, setMirrorExcluded] = useState<Set<number>>(new Set());
  // pageIndex -> employeeId escolhido manualmente
  const [manualSelections, setManualSelections] = useState<
    Record<number, number>
  >({});
  // pageIndex -> opções do Select (resultado da busca + item selecionado)
  const [manualOptions, setManualOptions] = useState<
    Record<number, { value: string; label: string }[]>
  >({});
  const manualSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const manualCount = Object.keys(manualSelections).length;
  const readyTotal = (importBatchPreview?.readyCount ?? 0) + manualCount;
  const failedTotal = (importBatchPreview?.failedCount ?? 0) - manualCount;

  const {
    fetch: deleteUserBiometricFetcher,
    loading: deleteUserBiometricLoader,
  } = useDeleteUserBiometric();

  function handleCloseModal() {
    setUserBiometric(undefined);
    setSelectUserId(undefined);
    setPageModal(null);
    formDismiss.reset();
    setSelectedUser([]);
  }

  function handleClearFilter() {
    formFilter.reset();
    formFilter.setFieldValue('inactive', false);
    setCreatedAtRange([null, null]);
    getUsers(true);
  }

  function handleSelectUserMirror() {
    if (!selectedUser.length) {
      errorNotification({
        title: 'Necessário selecionar usuários.',
        message: 'selecione da listagem',
      });

      return;
    }

    if (selectedUser.length > 15 && !formFilter.values.company) {
      errorNotification({
        title: 'Limite de usuário selecionados é de 15 por vez.',
        message: 'selecione um numero menor ou filtre por uma empresa',
      });

      return;
    }

    setPageModal('user-mirror');
  }

  function handleTabChange(value: string | null) {
    setActiveTab(value);
    if (value === 'pending') {
      getPendingFetcher({ onSuccess: setPendingData });
    }
  }

  function handleSendPayslipToSelected() {
    const ids = new Set(
      selectedUser.map((u) => u.employeeId).filter((id): id is number => !!id),
    );
    setPayslipFilterIds(ids);
    setPageModal('import-payslip');
  }

  function handlePageLimitChange(limitValue: number) {
    pageLimit.current = limitValue;
    getUsers();
  }

  function handlePageChange(limitValue: number) {
    page.current = limitValue;
    getUsers();
  }

  function handleOpenModal(userId: number, modalName: PageModalState) {
    setSelectUserId(userId);
    setPageModal(modalName);
  }

  function handleEdit(userType: UserType, userId: number) {
    if (userType !== UserType.USER) {
      if (user?.user.type !== UserType.MASTER) {
        errorNotification({
          title: 'Você não tem permissão para isto.',
          message: 'contate o master',
        });
        return;
      }
      rootNavigate(`users/access-edit/${userId}`);
    } else {
      rootNavigate(`users/edit/${userId}`);
    }
  }

  async function handleCloseMirrorModal() {
    setPageModal(null);
    setSelectedDate(CALENDAR_INITIAL_STATE);
    setEpiUsersToPrint([]);
  }

  async function handleUserMirrorExport() {
    if (selectedDate.at(0)?.getMonth() !== selectedDate.at(1)?.getMonth()) {
      errorNotification({
        title: 'Só é possivel exportar dados para 1 mês.',
        message: 'selecione um intervalo menor',
      });

      return;
    }

    if (selectedDate && selectedUser) {
      generateMirrorFetcher({
        params: {
          date: selectedDate.map((item) => item?.toISOString()).join(','),
          employees: selectedUser.map((userItem) => userItem.id).join(','),
          epiEmployees: epiUsersToPrint.length
            ? epiUsersToPrint.join(',')
            : undefined,
        },
        onSuccess: (data) => {
          downloadFile(
            data,
            `espelho-ponto-${String(new Date(Date.now()).valueOf()).slice(
              7,
              11,
            )}.pdf`,
          );
          setPageModal('confirm-mirror');
          setSelectedDate(CALENDAR_INITIAL_STATE);
          setSelectedUser([]);
          setEpiUsersToPrint([]);
          formFilter.reset();
        },
      });
    }
  }

  async function getUsers(reset = false) {
    const validatedValues: any = {};

    Object.entries(formFilter.values).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        validatedValues[key] = val;
      }
    });

    const dateParams: Record<string, string> = {};
    if (!reset && createdAtRange[0]) {
      dateParams.createdAtStart = createdAtRange[0].toISOString();
    }
    if (!reset && createdAtRange[1]) {
      dateParams.createdAtEnd = createdAtRange[1].toISOString();
    }

    await getUsersPaginatedFetcher({
      query: {
        page: page.current,
        limit: pageLimit.current,
        ...(reset ? { inactive: false } : validatedValues),
        ...dateParams,
      },
    });
  }

  async function handleRemoveUser(values: typeof formDismiss.values) {
    if (!selectedUserId) return;
    try {
      setPageLoading(true);
      await removeUserRequest(selectedUserId, {
        feedback: values.feedback,
        dismissReasons: values.dismissReasons,
      });
      setPageLoading(false);
      successNotification({
        title: 'Usuário dispensado!',
        message: 'tudo certo.',
      });
      getUsers();
      handleCloseModal();
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao dispensar usuário.',
        message: 'tente novamente',
      });
    }
  }

  async function handleRestoreUser() {
    if (!selectedUser.length) return;
    restoreUserFetcher({
      param: {
        id: selectedUser[0].id,
      },
      onSuccess: () => {
        successNotification({
          title: 'Usuário restaurado!',
          message: 'tudo certo',
        });
        getUsers();
        handleCloseModal();
      },
    });
  }

  async function handleCreateBiometric() {
    await createBiometricService()
      .then(async (resp) => {
        if (resp.data.success && selectedUserId) {
          await createBiometric({
            user: selectedUserId,
            template: resp.data.template,
          });
          successNotification({
            title: 'Biometria cadastrada com sucesso!',
            message: 'tudo certo',
          });
          handleCloseModal();
        }
      })
      .catch(() => {
        errorNotification({
          title: 'Erro ao criar biometria.',
          message: 'tente novamente.',
        });
      });
  }

  async function handleOpenBiometricModal(userId: number) {
    setSelectUserId(userId);
    setPageModal('biometric');
    await findBiometricByUser(userId).then((resp) => setUserBiometric(resp));
  }

  async function getCompanies() {
    try {
      setPageLoading(true);
      const response = await getCompaniesMinRequest();
      setCompanyList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar empresas.',
        message: 'tente novamente',
      });
    }
  }

  async function getWorkScales() {
    await getWorkScaleFetcher({});
  }

  async function handleAssignWorkScale({
    ...values
  }: typeof formAssignWorkScale.values) {
    if (selectedUserId) {
      try {
        setPageLoading(true);
        await updateEmployeeRequest(selectedUserId, {
          workScale: Number(values.workScale),
        });
        setPageLoading(false);
        successNotification({
          title: 'Escala atribuida com sucesso!',
          message: 'tudo certo',
        });
        handleCloseModal();
        formAssignWorkScale.reset();
      } catch (error) {
        setPageLoading(false);
        errorNotification({
          title: 'Erro ao atribuir escala ao usuário.',
          message: 'tente novamente.',
        });
      }
    }
  }

  async function handleRemoveManyUsers() {
    await removeManyUserFetcher({
      query: {
        ids: selectedUser.map((userItem) => userItem.id).join(','),
      },
      onSuccess: () => {
        getUsers();
        setSelectedUser([]);
        successNotification({
          title: 'Usuários inativados!',
          message: 'tudo certo',
        });
      },
    });
  }

  async function handleViewEmployeeFiles(employeeId: number) {
    setSelectUserId(employeeId);
    await getEmployeeFilesFetcher({
      employeeId: employeeId,
    });
    await getEmployeePayslipFilesFetcher({
      employeeId: employeeId,
    });
    setPageModal('files');
  }

  async function handleUploadFiles(
    fileList: File[],
    errorMessage: string,
  ): Promise<number[]> {
    let fileIds: number[] = [];

    if (!fileList.length) return [];

    try {
      await uploadFilesFetcher({
        data: {
          files: fileList,
        },
        onSuccess: (res) => {
          fileIds = res;
        },
      });
      return fileIds;
    } catch (error) {
      errorNotification({
        title: 'Erro ao realizar upload dos arquivos',
        message: errorMessage,
      });
      return [];
    }
  }

  async function handleAddEmployeeFile(values: typeof formAddFile.values) {
    const { files, type } = values;

    if (!type) {
      errorNotification({
        title: 'Selecione o tipo de arquivo',
        message: 'Tente novamente',
      });
      return;
    }

    if (!files.length) {
      errorNotification({
        title: '',
        message: 'Selecione ao menos um arquivo',
      });
      return;
    }

    let fileIds: number[] = [];

    fileIds = await handleUploadFiles(files, 'Tente novamente.');

    if (type === 'payslip') {
      handleAddEmployeePayslipFile(fileIds);
      return;
    }

    if (type === 'document' || type === 'epi') {
      await addEmployeeFilesFetcher({
        employeeId: Number(selectedUserId),
        data: {
          files: fileIds.map((fileId) => fileId),
          category: type,
        },
        onSuccess: () => {
          successNotification({
            title: 'Arquivos adicionados com sucesso!',
            message: 'tudo certo',
          });
          formAddFile.reset();
          setAddFileCollapse(false);
          handleViewEmployeeFiles(Number(selectedUserId));
        },
      });
    }
  }

  async function handleDeleteUserBiometric() {
    if (!selectedUserId) return;
    await deleteUserBiometricFetcher({
      id: Number(selectedUserId),
      onSuccess: () => {
        successNotification({
          title: 'Biometria excluida com sucesso!',
          message: 'tudo certo',
        });
        handleCloseModal();
      },
    });
  }

  async function handlePreviewBatchPayslip(file: File) {
    setImportPreviewFile(file);
    setPageModal(null);
    await previewBatchPayslipFetcher({
      file,
      onSuccess: (preview) => {
        setImportBatchPreview(preview);
        setPageModal('import-payslip-preview');
      },
    });
  }

  async function handleConfirmBatchPayslip() {
    if (!importPreviewFile) return;
    const mirrorPageIndexes =
      importReferenceMonth && sendMirrorGlobal && importBatchPreview
        ? importBatchPreview.pages
            .filter(
              (p) =>
                (p.found || !!manualSelections[p.pageIndex]) &&
                !mirrorExcluded.has(p.pageIndex),
            )
            .map((p) => p.pageIndex)
        : undefined;

    await importBatchPayslipFetcher({
      file: importPreviewFile,
      overrides: manualSelections,
      referenceMonth: importReferenceMonth ?? undefined,
      mirrorPageIndexes,
      onSuccess: (result) => {
        setImportBatchPreview(null);
        setImportPreviewFile(null);
        setManualSelections({});
        setManualOptions({});
        setImportReferenceMonth(null);
        setSendMirrorGlobal(false);
        setMirrorExcluded(new Set());
        setImportBatchResult(result);
        setPageModal('import-payslip-result');
      },
    });
  }

  function handleManualSearch(pageIndex: number, term: string) {
    if (manualSearchTimer.current) clearTimeout(manualSearchTimer.current);
    if (term.trim().length < 2) return;
    manualSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await searchPayslipEmployeesRequest(term.trim());
        setManualOptions((prev) => {
          const selectedId = manualSelections[pageIndex];
          const kept = (prev[pageIndex] ?? []).filter(
            (o) => Number(o.value) === selectedId,
          );
          const fetched = data
            .filter((e) => e.id !== selectedId)
            .map((e) => ({ value: String(e.id), label: e.name }));
          return { ...prev, [pageIndex]: [...kept, ...fetched] };
        });
      } catch {
        errorNotification({
          title: 'Erro ao buscar funcionários.',
          message: 'Tente novamente.',
        });
      }
    }, 300);
  }

  function handleManualSelect(pageIndex: number, value: string | null) {
    setManualSelections((prev) => {
      const next = { ...prev };
      if (value) next[pageIndex] = Number(value);
      else delete next[pageIndex];
      return next;
    });
  }

  function handleCloseImportModal() {
    setPageModal(null);
    setImportPreviewFile(null);
    setImportBatchPreview(null);
    setImportBatchResult(null);
    setExpandedPage(null);
    setManualSelections({});
    setManualOptions({});
    setPayslipFilterIds(null);
  }

  async function handleAddEmployeePayslipFile(fileIds: number[]) {
    await uploadEmployeePayslipFetcher({
      data: {
        employeeId: Number(selectedUserId),
        documentFile: fileIds[0],
      },
      onSuccess: () => {
        successNotification({
          title: 'Arquivos adicionados com sucesso!',
          message: 'tudo certo',
        });
        formAddFile.reset();
        setAddFileCollapse(false);
        handleViewEmployeeFiles(Number(selectedUserId));
      },
    });
  }

  async function handleUnlinkEpi(id: number) {
    await unlinkEpiFetcher(id, () => {
      successNotification({
        title: 'Sucesso',
        message: 'EPI desvinculado com sucesso',
      });
      if (selectedUserId) {
        listEmployeeEpisFetcher({ employeeId: Number(selectedUserId) });
      }
      getUsers();
    });
  }

  async function handlePrintEpiReceipt() {
    if (!selectedUserId) return;
    try {
      setPrintEpiLoading(true);
      const response = await exportEpiReceiptRequest(selectedUserId);
      downloadFile(response.data, `termo-epi-${selectedUserId}.pdf`);
    } catch {
      errorNotification({
        title: 'Erro ao gerar documento',
        message: 'Tente novamente',
      });
    } finally {
      setPrintEpiLoading(false);
    }
  }

  const [historyPdfLoading, setHistoryPdfLoading] = useState(false);

  async function handlePrintEpiHistory() {
    if (!selectedUserId) return;
    try {
      setHistoryPdfLoading(true);
      const response = await exportEpiHistoryReceiptRequest(selectedUserId);
      downloadFile(response.data, `historico-epi-${selectedUserId}.pdf`);
    } catch {
      errorNotification({
        title: 'Erro ao gerar histórico',
        message: 'Tente novamente',
      });
    } finally {
      setHistoryPdfLoading(false);
    }
  }

  async function handleToggleRegisterTime(
    userId: number,
    value: boolean,
    blockMessage: string,
  ) {
    try {
      await updateUserRequest(userId, {
        canRegisterTime: value,
        registerTimeBlockMessage: blockMessage,
      });
      successNotification({
        title: 'Sucesso',
        message: 'Status de marcação de ponto atualizado',
      });
      getUsers();
    } catch (error) {
      errorNotification({
        title: 'Erro ao atualizar status',
        message: 'tente novamente',
      });
    } finally {
      setPageModal(null);
      setPendingRegisterTimeChange(null);
      setRegisterTimeBlockMessage('');
    }
  }

  useEffect(() => {
    getCompanies();
    getWorkScales();
    getUsers();
  }, []);

  return (
    <Page>
      <CustomLoader
        loading={
          restoreUserLoader ||
          getUsersPaginatedLoader ||
          generateMirrorLoader ||
          getWorkScaleLoading ||
          removeManyUserLoader
        }
      />
      <Flex m={16} direction="column">
        <Tabs value={activeTab} onTabChange={handleTabChange}>
          <Tabs.List mb="md">
            <Tabs.Tab value="users">Funcionários</Tabs.Tab>
            <Tabs.Tab value="pending">
              Pendências
              {pendingData &&
                pendingData.unsigned.length + pendingData.notSent.length >
                  0 && (
                  <Badge
                    ml={6}
                    size="xs"
                    color="red"
                    variant="filled"
                    radius="xl"
                  >
                    {pendingData.unsigned.length + pendingData.notSent.length}
                  </Badge>
                )}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="users">
            <Flex align="center" justify="end" mb={8}>
              <Menu>
                <Menu.Target>
                  <Button mr={8}>Ações</Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconClipboardData size={14} />}
                    onClick={handleSelectUserMirror}
                    disabled={!selectedUser.length}
                  >
                    Espelho
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconFile size={14} />}
                    onClick={handleSendPayslipToSelected}
                    disabled={!selectedUser.length}
                  >
                    Enviar Holerite
                  </Menu.Item>
                  <Menu.Item
                    color="red"
                    icon={<IconTrash size={14} />}
                    onClick={handleRemoveManyUsers}
                    disabled={!selectedUser.length}
                  >
                    Remover
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Button
                mr={8}
                color="teal"
                leftIcon={<IconFileImport size={16} />}
                loading={previewBatchPayslipLoader}
                onClick={() => setPageModal('import-payslip')}
              >
                Importar Holerites
              </Button>
              <Button
                mr={8}
                color="ltpBlue.9"
                leftIcon={<IconCirclePlus />}
                onClick={() => rootNavigate(AppRoutePaths.USERS_CREATE)}
              >
                Novo
              </Button>
            </Flex>
            <Flex wrap="wrap" justify="start" mb={8}>
              <form onSubmit={formFilter.onSubmit(() => getUsers())}>
                <Flex align="center">
                  <TextInput
                    label="Usuário"
                    placeholder="nome do usuário"
                    type="text"
                    name="username"
                    mb={16}
                    mr={8}
                    {...formFilter.getInputProps('username')}
                  />
                  <Select
                    clearable
                    name="role"
                    label="Tipo de usuário"
                    placeholder="selecione o tipo e usuário"
                    data={userRoleList}
                    {...formFilter.getInputProps('role')}
                    mb={16}
                    mr={8}
                  />
                  <Select
                    searchable
                    clearable
                    label="Empresa"
                    placeholder="selecione a empresa"
                    data={companyList?.map((item) => ({
                      label: item.fantasyName,
                      value: String(item.id),
                    }))}
                    mb={16}
                    mr={8}
                    {...formFilter.getInputProps('company')}
                  />
                  <Select
                    clearable
                    label="Escala"
                    name="workScale"
                    placeholder="selecione a escala"
                    data={
                      getWorkScaleData?.map((item) => ({
                        label: item.name,
                        value: String(item.id),
                      })) ?? []
                    }
                    mb={16}
                    mr={8}
                    {...formFilter.getInputProps('workScale')}
                  />
                  <CustomDateRangePicker
                    label="Cadastrado entre"
                    placeholder="selecione o período"
                    value={createdAtRange}
                    onChange={setCreatedAtRange}
                    mr={8}
                    mb={16}
                    clearable
                  />
                  <Button mb={-10} type="submit">
                    Filtrar
                  </Button>
                  <Button
                    mb={-10}
                    ml={10}
                    onClick={handleClearFilter}
                    color="ltpBlue.9"
                    variant="outline"
                  >
                    Limpar
                  </Button>
                  <Checkbox
                    label="Inativos"
                    name="inactive"
                    color="blue"
                    ml={8}
                    mb={-5}
                    checked={formFilter.values.inactive}
                    {...formFilter.getInputProps('inactive')}
                  />
                </Flex>
              </form>
            </Flex>
            <DataTable
              recordsPerPage={getUsersPaginatedData?.meta.itemsPerPage ?? 5}
              selectedRecords={selectedUser}
              onSelectedRecordsChange={(selectedUser) =>
                setSelectedUser(selectedUser)
              }
              recordsPerPageOptions={[10, 50, 100, 500]}
              onRecordsPerPageChange={(recordRange) =>
                handlePageLimitChange(recordRange)
              }
              onPageChange={handlePageChange}
              page={page.current}
              totalRecords={getUsersPaginatedData?.meta.totalItems}
              fetching={pageLoading}
              height="62vh"
              noRecordsText="Sem usuários"
              withBorder
              borderRadius="sm"
              striped
              highlightOnHover
              recordsPerPageLabel="Itens por página"
              records={getUsersPaginatedData?.items}
              /*
          rowStyle={(userRow) => {
            const needsEpi = [
              UserRole.EMPLOYEE,
              UserRole.DAILY_EMPLOYEE,
              UserRole.MONTHLY_EMPLOYEE,
              UserRole.CLEANING_EMPLOYEE,
            ].includes(userRow.role as UserRole);

            const hasNoEpi = Number(userRow.epiCount) === 0;

            if (needsEpi && hasNoEpi) {
              return {
                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                color: 'red',
              };
            }
            return {};
          }}*/
              columns={[
                {
                  accessor: 'username',
                  title: 'Nome',
                  render: (userRow) => {
                    const needsEpi = [
                      UserRole.EMPLOYEE,
                      UserRole.DAILY_EMPLOYEE,
                      UserRole.MONTHLY_EMPLOYEE,
                      UserRole.CLEANING_EMPLOYEE,
                    ].includes(userRow.role as UserRole);

                    const hasNoEpi = Number(userRow.epiCount) === 0;

                    return (
                      <Flex align="center" gap="xs">
                        <Text>{userRow.username}</Text>
                        {needsEpi && hasNoEpi && (
                          <Tooltip
                            label="Usuário sem EPI Entregue"
                            withinPortal
                            withArrow
                            zIndex={1000}
                          >
                            <div style={{ display: 'flex', cursor: 'help' }}>
                              <IconAlertCircle size={16} color="red" />
                            </div>
                          </Tooltip>
                        )}
                      </Flex>
                    );
                  },
                },
                {
                  accessor: 'role',
                  title: 'Tipo',
                  render: ({ role }) => (
                    <Badge>{userRoleHumanized[role]}</Badge>
                  ),
                },
                {
                  accessor: 'accessCode',
                  title: 'Código de acesso',
                },
                {
                  accessor: 'deletedAt',
                  title: 'Status',
                  render: ({ deletedAt }) => (
                    <Badge color={deletedAt ? 'red' : 'blue'}>
                      {deletedAt ? 'inativo' : 'ativo'}
                    </Badge>
                  ),
                },
                {
                  accessor: 'dismissReasons',
                  title: 'Motivos',
                  render: ({ dismissReasons }) => {
                    if (!dismissReasons) return null;
                    const reasons = dismissReasons.split(',');
                    return (
                      <Popover
                        width={220}
                        position="bottom-start"
                        withinPortal
                        withArrow
                        shadow="md"
                      >
                        <Popover.Target>
                          <Badge
                            color="red"
                            variant="filled"
                            style={{ cursor: 'pointer' }}
                          >
                            {reasons.length}{' '}
                            {reasons.length === 1 ? 'motivo' : 'motivos'}
                          </Badge>
                        </Popover.Target>
                        <Popover.Dropdown>
                          <Text size="xs" fw={600} mb={6} color="red">
                            Motivos da dispensa
                          </Text>
                          <List size="xs" spacing={4}>
                            {reasons.map((r) => (
                              <List.Item key={r}>{r}</List.Item>
                            ))}
                          </List>
                        </Popover.Dropdown>
                      </Popover>
                    );
                  },
                },
                {
                  accessor: 'sector',
                  title: 'Setor',
                },
                {
                  accessor: 'canRegisterTime',
                  title: 'Marca Ponto',
                  render: (userRow) => (
                    <Switch
                      checked={userRow.canRegisterTime !== false}
                      onChange={(event) => {
                        const newValue = event.currentTarget.checked;
                        setPendingRegisterTimeChange({
                          id: userRow.id,
                          value: newValue,
                        });
                        setPageModal('confirm-register-time');
                      }}
                    />
                  ),
                },
                {
                  accessor: 'companyTaker',
                  title: 'Empresa',
                },
                {
                  accessor: 'workScale',
                  title: 'Escala',
                },
                {
                  accessor: 'paymentPrice',
                  title: 'Diária',
                  render: ({ paymentPrice }) => {
                    if (!paymentPrice || Number(paymentPrice) === 0) return '-';
                    return formatBRL(Number(paymentPrice).toFixed(2));
                  },
                },
                {
                  accessor: 'transportPrice',
                  title: 'Transporte',
                  render: ({ transportPrice }) => {
                    if (!transportPrice || Number(transportPrice) === 0)
                      return '-';
                    return formatBRL(Number(transportPrice).toFixed(2));
                  },
                },
                {
                  accessor: 'createdAt',
                  title: 'Criado em',
                  render: ({ createdAt }) =>
                    formatLocale(createdAt, 'DD/MM/YYYY'),
                },
                {
                  accessor: '',
                  title: 'Arquivos',
                  render: ({ employeeId }) => (
                    <Button
                      color="blue"
                      variant="subtle"
                      onClick={() => handleViewEmployeeFiles(employeeId)}
                      w={40}
                      p={0}
                    >
                      <IconFile />
                    </Button>
                  ),
                  width: 110,
                },
                {
                  accessor: '',
                  title: 'EPI',
                  render: ({ employeeId }) => (
                    <Button
                      color="green"
                      variant="subtle"
                      onClick={() => {
                        setSelectUserId(employeeId);
                        setPageModal('link-epi');
                        listEmployeeEpisFetcher({ employeeId, limit: 5000 });
                        listEpiDeliveriesFetcher({ employeeId, limit: 5000 });
                      }}
                      w={40}
                      p={0}
                      disabled={!employeeId}
                    >
                      <IconShield />
                    </Button>
                  ),
                  width: 110,
                },
                {
                  accessor: '',
                  title: '',
                  render: (userRow) => (
                    <Menu>
                      <Menu.Target>
                        <Button color="blue" variant="subtle" w={40} p={0}>
                          <IconDotsVertical />
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown style={{ position: 'absolute' }}>
                        <Menu.Item
                          onClick={() => {
                            handleOpenModal(userRow.id, 'work-scale');
                          }}
                          icon={<IconCalendarDue size={14} />}
                          disabled={user?.user.type !== UserType.MASTER}
                        >
                          Escala
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => {
                            handleOpenBiometricModal(userRow.id);
                          }}
                          icon={<IconFingerprint size={14} />}
                          disabled={
                            user?.user.role &&
                            ![UserRole.MANAGER_CREATOR, UserRole.NONE].includes(
                              user?.user.role,
                            )
                          }
                        >
                          Biometria
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => handleEdit(userRow.type, userRow.id)}
                          icon={<IconEdit size={14} />}
                        >
                          Editar
                        </Menu.Item>
                        <Menu.Item
                          disabled={userRow.deletedAt !== null}
                          onClick={() => handleOpenModal(userRow.id, 'dismiss')}
                          icon={<IconUserOff size={14} />}
                        >
                          Dispensar
                        </Menu.Item>
                        <Menu.Item
                          disabled={userRow.deletedAt === null}
                          onClick={() => {
                            setSelectedUser([userRow]);
                            setPageModal('restore');
                          }}
                          icon={<IconUserCheck size={14} />}
                        >
                          Restaurar
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  ),
                },
              ]}
            />
          </Tabs.Panel>

          <Tabs.Panel value="pending" pt="md">
            <Stack spacing="lg">
              <Group position="apart">
                <Text fw={600} size="sm">
                  Pendências de Holerites
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  loading={pendingLoader}
                  onClick={() =>
                    getPendingFetcher({ onSuccess: setPendingData })
                  }
                >
                  Atualizar
                </Button>
              </Group>

              {pendingLoader && !pendingData && (
                <Stack spacing="xs">
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                </Stack>
              )}

              {pendingData && (
                <>
                  <div>
                    <Text size="sm" fw={600} color="orange" mb="xs">
                      Aguardando assinatura ({pendingData.unsigned.length})
                    </Text>
                    {pendingData.unsigned.length === 0 ? (
                      <Text size="sm" color="dimmed">
                        Nenhuma pendência
                      </Text>
                    ) : (
                      <Stack spacing={4}>
                        {pendingData.unsigned.map((item) => (
                          <Flex
                            key={item.payslipId}
                            align="center"
                            justify="space-between"
                            style={{
                              padding: '8px 12px',
                              background: '#fff8f0',
                              borderRadius: 6,
                              border: '1px solid #ffe8cc',
                            }}
                          >
                            <Text size="sm">{item.employeeName}</Text>
                            <Text size="xs" color="dimmed">
                              Enviado em{' '}
                              {formatLocale(item.createdAt, 'DD/MM/YYYY')}
                            </Text>
                          </Flex>
                        ))}
                      </Stack>
                    )}
                  </div>

                  <div>
                    <Text size="sm" fw={600} color="red" mb="xs">
                      Aguardando envio ({pendingData.notSent.length})
                    </Text>
                    {pendingData.notSent.length === 0 ? (
                      <Text size="sm" color="dimmed">
                        Nenhuma pendência
                      </Text>
                    ) : (
                      <Stack spacing={4}>
                        {pendingData.notSent.map((item) => (
                          <Flex
                            key={item.employeeId}
                            align="center"
                            style={{
                              padding: '8px 12px',
                              background: '#fff5f5',
                              borderRadius: 6,
                              border: '1px solid #ffc9c9',
                            }}
                          >
                            <Text size="sm">{item.employeeName}</Text>
                          </Flex>
                        ))}
                      </Stack>
                    )}
                  </div>
                </>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Flex>
      <Modal
        title="Exportar Espelho de Ponto:"
        opened={pageModal === 'user-mirror'}
        onClose={handleCloseMirrorModal}
        closeOnClickOutside={false}
        closeOnEscape={false}
        size="70%"
      >
        <Grid columns={5} mb={18}>
          <Grid.Col span={2}>
            <Group position="center">
              <RangeCalendar
                locale="pt-br"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
              />
              <Group position="left">
                <Button
                  onClick={() => setSelectedDate(CALENDAR_INITIAL_STATE)}
                  variant="subtle"
                >
                  limpar
                </Button>
              </Group>
            </Group>
          </Grid.Col>
          <Grid.Col span={3}>
            <DataTable
              fetching={pageLoading}
              minHeight={200}
              noRecordsText="Sem funcionários"
              withBorder
              borderRadius="sm"
              striped
              highlightOnHover
              records={selectedUser}
              columns={[
                {
                  accessor: 'username',
                  title: 'Nome',
                },
                {
                  accessor: 'role',
                  title: 'Tipo',
                  render: ({ role }) => (
                    <Badge>{userRoleHumanized[role]}</Badge>
                  ),
                },
                {
                  accessor: 'accessCode',
                  title: 'Código de acesso',
                },
                {
                  accessor: 'epi',
                  title: 'Imprimir Termo de EPI?',
                  textAlignment: 'center',
                  render: (record) => (
                    <Checkbox
                      checked={epiUsersToPrint.includes(record.id)}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setEpiUsersToPrint((prev) => [...prev, record.id]);
                        } else {
                          setEpiUsersToPrint((prev) =>
                            prev.filter((id) => id !== record.id),
                          );
                        }
                      }}
                    />
                  ),
                },
              ]}
            />
          </Grid.Col>
        </Grid>
        <Group position="right">
          <Button
            disabled={!selectedDate}
            onClick={() => handleUserMirrorExport()}
          >
            Gerar
          </Button>
        </Group>
        <Text fw="bold">Instruções:</Text>
        <Text>
          1- selecione a data e logo após verifique os usuários que deseja gerar
          o documento.
        </Text>
        <Text>2- logo aṕos, o documento será baixado.</Text>
      </Modal>
      <Modal
        title="Exportar Recibo."
        opened={pageModal === 'confirm-mirror'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={false}
        closeOnEscape={false}
      >
        <Group>
          <Text fw="bold">Tudo certo!</Text>
          <Text>O documento vai aparecer em instantes no seus Downloads.</Text>
        </Group>
        <Group position="right">
          <Button onClick={() => setPageModal(null)}>OK</Button>
        </Group>
      </Modal>
      <Modal
        title="Biometria"
        opened={pageModal === 'biometric'}
        onClose={() => {
          handleCloseModal();
        }}
        size="30%"
      >
        <Flex direction={'column'}>
          {!userBiometric?.template && (
            <Text fw={'bold'} mb={'xs'}>
              Sem biometria cadastrada para este usuário
            </Text>
          )}

          {userBiometric?.template && (
            <Text fw={'bold'} mb={'xs'}>
              Biometria já cadastrada!
            </Text>
          )}

          <Flex gap={'lg'} justify={'center'}>
            <Button
              color="red"
              fullWidth
              onClick={() => setPageModal('delete-biometric')}
              disabled={!userBiometric?.template || user?.user.id !== 1009}
            >
              Excluir
            </Button>
            <Button onClick={() => handleCreateBiometric()} fullWidth>
              Novo
            </Button>
          </Flex>
        </Flex>
      </Modal>
      <Modal
        title="Atribuir escala"
        opened={pageModal === 'work-scale'}
        onClose={() => {
          handleCloseModal();
        }}
        size="30%"
      >
        <Flex direction={'column'}>
          <form
            onSubmit={formAssignWorkScale.onSubmit((values) =>
              handleAssignWorkScale(values),
            )}
          >
            <Select
              clearable
              name="workScale"
              placeholder="selecione a escala"
              data={
                getWorkScaleData?.map((item) => ({
                  label: item.name,
                  value: String(item.id),
                })) ?? []
              }
              mb={16}
              mr={8}
              {...formAssignWorkScale.getInputProps('workScale')}
            />
            <Group mt={16} mb={16} position="right">
              <Button type="submit">Salvar</Button>
            </Group>
          </form>
        </Flex>
      </Modal>
      <Modal
        size={660}
        title="Arquivos do funcionário"
        opened={pageModal === 'files'}
        onClose={() => setPageModal(null)}
      >
        <Collapse in={addFileCollapse}>
          <form
            onSubmit={formAddFile.onSubmit((values) =>
              handleAddEmployeeFile(values),
            )}
          >
            <Grid columns={2}>
              <Grid.Col span={2}>
                <Select
                  clearable
                  name="type"
                  label="Tipo"
                  placeholder="selecione o tipo"
                  data={[
                    { value: 'document', label: 'Documento' },
                    { value: 'epi', label: 'EPI' },
                    { value: 'payslip', label: 'Holerite' },
                  ]}
                  required
                  {...formAddFile.getInputProps('type')}
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <CustomMultipleFileButton
                  name="files"
                  accept="application/pdf,image/png,image/jpeg,image/jpg,.eps,.ai,.cdr,.svg,.ps,.bin"
                  formRef={formAddFile}
                  textInput="Clique para anexar o arquivo"
                  icon={<IconFileInfo size={24} />}
                />
              </Grid.Col>
            </Grid>
            <Group mt={'md'} mb={'md'}>
              <Button
                fullWidth
                type="submit"
                color="ltpBlue.9"
                disabled={!formAddFile.values.files.length}
                loading={
                  uploadFilesLoader ||
                  addEmployeeFilesLoading ||
                  uploadEmployeePayslipLoader
                }
              >
                Salvar
              </Button>
            </Group>
          </form>
        </Collapse>

        <Tabs defaultValue="documents" variant="outline">
          <Tabs.List>
            <Tabs.Tab value="documents">
              Documentos
              {getEmployeeFilesData?.filter(
                (f) => !f.category || f.category === 'document',
              ).length ? (
                <Badge ml={6} size="xs" variant="filled" color="blue">
                  {
                    getEmployeeFilesData.filter(
                      (f) => !f.category || f.category === 'document',
                    ).length
                  }
                </Badge>
              ) : null}
            </Tabs.Tab>
            <Tabs.Tab value="epi">
              EPIs
              {getEmployeeFilesData?.filter((f) => f.category === 'epi')
                .length ? (
                <Badge ml={6} size="xs" variant="filled" color="blue">
                  {
                    getEmployeeFilesData.filter((f) => f.category === 'epi')
                      .length
                  }
                </Badge>
              ) : null}
            </Tabs.Tab>
            <Tabs.Tab value="payslips">
              Holerites
              {getEmployeePayslipFilesData?.length ? (
                <Badge ml={6} size="xs" variant="filled" color="blue">
                  {getEmployeePayslipFilesData.length}
                </Badge>
              ) : null}
            </Tabs.Tab>
          </Tabs.List>

          <Group position="right" mt="xs">
            <Button
              size="xs"
              variant="light"
              onClick={() => setAddFileCollapse((o) => !o)}
            >
              {addFileCollapse ? 'Cancelar' : '+ Adicionar'}
            </Button>
          </Group>

          <Tabs.Panel value="documents" pt="sm">
            {getEmployeeFilesLoading ? (
              <Skeleton height={80} radius="md" />
            ) : getEmployeeFilesData?.filter(
                (f) => !f.category || f.category === 'document',
              ).length ? (
              getEmployeeFilesData
                .filter((f) => !f.category || f.category === 'document')
                .map((file) => <CardFile key={file.id} item={file} />)
            ) : (
              <Flex align="center" justify="center" py="xl">
                <Text color="dimmed" size="sm">
                  Nenhum documento cadastrado
                </Text>
              </Flex>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="epi" pt="sm">
            {getEmployeeFilesLoading ? (
              <Skeleton height={80} radius="md" />
            ) : getEmployeeFilesData?.filter((f) => f.category === 'epi')
                .length ? (
              getEmployeeFilesData
                .filter((f) => f.category === 'epi')
                .map((file) => <CardFile key={file.id} item={file} />)
            ) : (
              <Flex align="center" justify="center" py="xl">
                <Text color="dimmed" size="sm">
                  Nenhum EPI cadastrado
                </Text>
              </Flex>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="payslips" pt="sm">
            {getEmployeePayslipFilesLoading ? (
              <Skeleton height={80} radius="md" />
            ) : getEmployeePayslipFilesData?.length ? (
              getEmployeePayslipFilesData.map((file) => (
                <CardPayslipFile
                  key={file.id}
                  item={file}
                  onSignedDocumentCreated={() =>
                    getEmployeePayslipFilesFetcher({
                      employeeId: Number(selectedUserId),
                    })
                  }
                  onDeleted={() =>
                    getEmployeePayslipFilesFetcher({
                      employeeId: Number(selectedUserId),
                    })
                  }
                />
              ))
            ) : (
              <Flex align="center" justify="center" py="xl">
                <Text color="dimmed" size="sm">
                  Nenhum holerite cadastrado
                </Text>
              </Flex>
            )}
          </Tabs.Panel>
        </Tabs>
      </Modal>

      <Modal
        title="Confirmar"
        opened={pageModal === 'delete-biometric'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Text fz="md" mb={16} fw={'bold'}>
          Deseja realmente excluir a biometria?
        </Text>
        <Button color="red" onClick={() => setPageModal(null)} mr={16}>
          Voltar
        </Button>
        <Button
          color="green"
          onClick={handleDeleteUserBiometric}
          loading={deleteUserBiometricLoader}
        >
          Confirmar
        </Button>
      </Modal>

      <Modal
        title="Dispensar funcionário"
        opened={pageModal === 'dismiss'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Flex direction={'column'}>
          <form
            onSubmit={formDismiss.onSubmit((values) =>
              handleRemoveUser(values),
            )}
          >
            <MultiSelect
              label="Motivos da dispensa"
              description="Selecione um ou mais motivos"
              placeholder="Selecione os motivos"
              mb={16}
              data={[
                'Má conduta',
                'Atrasos',
                'Faltas',
                'Cancelamento de última hora',
                'Desistência',
                'Baixo comprometimento',
                'Baixa produtividade',
                'Desempenho insatisfatório',
                'Descumprimento de normas',
                'Não uso de EPI',
                'Desrespeito',
                'Discussão/briga',
                'Conflitos com colegas',
                'Problemas com liderança',
                'Uso inadequado do celular',
                'Recusa de atividades',
                'Abandono da diária',
                'Não recomendado para novas diárias',
              ]}
              {...formDismiss.getInputProps('dismissReasons')}
            />
            <Textarea
              label="Feedback do funcionário"
              description="Observações adicionais sobre a dispensa"
              placeholder="observação sobre a dispensa"
              name="feedback"
              mb={16}
              mr={8}
              maxLength={255}
              {...formDismiss.getInputProps('feedback')}
            />
            <Group mt={16} mb={16} position="right">
              <Button type="submit">Salvar</Button>
            </Group>
          </form>
        </Flex>
      </Modal>

      <Modal
        title="EPIs do funcionário"
        opened={pageModal === 'link-epi'}
        onClose={() => {
          handleCloseModal();
        }}
        size="xl"
      >
        <ScrollArea h={480} offsetScrollbars>
          {!employeeEpis?.items?.length ? (
            <Text color="dimmed" align="center" py={32}>
              Nenhum EPI vinculado a este funcionário.
            </Text>
          ) : (
            <Stack spacing={8}>
              {employeeEpis.items.map((record) => {
                const deliveries = (epiDeliveries?.items ?? []).filter(
                  (d) => d.epi.id === record.epi?.id,
                );
                const lastDelivery = deliveries.sort(
                  (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime(),
                )[0];
                const lastDate = lastDelivery?.createdAt ?? null;

                const replacementDays = record.epi?.replacementPeriodDays;
                let status: 'entregue' | 'pendente' | 'atrasado' | 'sem-prazo' =
                  'pendente';
                if (deliveries.length > 0) {
                  if (!replacementDays) {
                    status = 'sem-prazo';
                  } else {
                    const daysSince = dayjsPlugins().diff(
                      dayjsPlugins(lastDate),
                      'day',
                    );
                    status =
                      daysSince > replacementDays ? 'atrasado' : 'entregue';
                  }
                }

                const statusMap = {
                  entregue: { color: 'green', label: 'Entregue' },
                  pendente: { color: 'yellow', label: 'Pendente' },
                  atrasado: { color: 'red', label: 'Atrasado' },
                  'sem-prazo': { color: 'gray', label: 'Sem prazo' },
                };

                return (
                  <Group
                    key={record.id}
                    position="apart"
                    px={12}
                    py={10}
                    sx={(theme) => ({
                      border: `1px solid ${theme.colors.gray[3]}`,
                      borderRadius: theme.radius.sm,
                      backgroundColor: theme.white,
                    })}
                  >
                    <div>
                      <Group spacing={8} mb={2}>
                        <Text size="sm" fw={500}>
                          {record.epi?.name ?? '—'}
                        </Text>
                        {record.epi?.category && (
                          <Badge color="blue" variant="light" size="xs">
                            {record.epi.category}
                          </Badge>
                        )}
                      </Group>
                      {(record.epi?.brand || record.epi?.ca) && (
                        <Text size="xs" color="dimmed">
                          {[
                            record.epi?.brand,
                            record.epi?.ca ? `CA ${record.epi.ca}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                      )}
                      <Text
                        size="xs"
                        color={lastDate ? 'teal' : 'dimmed'}
                        fw={lastDate ? 500 : undefined}
                      >
                        {lastDate
                          ? `Entregue em ${formatLocale(
                              lastDate,
                              'DD/MM/YYYY',
                            )}`
                          : 'Sem entrega registrada'}
                      </Text>
                    </div>
                    <Group spacing={8}>
                      <Badge
                        color={statusMap[status].color}
                        variant="filled"
                        size="sm"
                      >
                        {statusMap[status].label}
                      </Badge>
                      <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        onClick={() => setDeliverModalRecordId(record.id)}
                      >
                        Marcar Entregue
                      </Button>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => handleUnlinkEpi(record.id)}
                        loading={unlinkEpisLoading}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>
                );
              })}
            </Stack>
          )}
        </ScrollArea>
        <Group position="apart" mt="md">
          <Group spacing={8}>
            <Button
              variant="subtle"
              leftIcon={<IconFile size={16} />}
              loading={printEpiLoading}
              disabled={!employeeEpis?.items?.length}
              onClick={handlePrintEpiReceipt}
            >
              Termo de EPI
            </Button>
            <Button
              variant="subtle"
              color="teal"
              leftIcon={<IconFile size={16} />}
              loading={historyPdfLoading}
              disabled={!epiDeliveries?.items?.length}
              onClick={handlePrintEpiHistory}
            >
              Ficha de Histórico
            </Button>
          </Group>
        </Group>
      </Modal>

      <Modal
        title="Restaurar funcionário"
        opened={pageModal === 'restore'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={false}
        size={600}
      >
        <Flex direction={'column'}>
          <Text fw={'bold'}>Informações sobre o funcionário</Text>
          {selectedUser[0]?.dismissReasons && (
            <>
              <Text size={'sm'} fw={500} mt={8}>
                Motivos da dispensa:
              </Text>
              <Flex gap={4} wrap="wrap" mt={4} mb={8}>
                {selectedUser[0].dismissReasons.split(',').map((reason) => (
                  <Badge key={reason} color="red" variant="light">
                    {reason}
                  </Badge>
                ))}
              </Flex>
            </>
          )}
          {selectedUser[0]?.feedback && (
            <>
              <Text size={'sm'} fw={500}>
                Observações:
              </Text>
              <Text size={'md'} color="dimmed">
                {selectedUser[0].feedback}
              </Text>
            </>
          )}
          <Group mt={16} mb={16} position="right">
            <Button onClick={() => handleRestoreUser()}>Restaurar</Button>
          </Group>
        </Flex>
      </Modal>

      <Modal
        title="Alterar marcação de ponto"
        opened={pageModal === 'confirm-register-time'}
        onClose={() => {
          setPageModal(null);
          setRegisterTimeBlockMessage('');
        }}
        centered
      >
        <Text mb={16}>
          Tem certeza de que deseja{' '}
          {pendingRegisterTimeChange?.value ? 'habilitar' : 'desabilitar'} a
          marcação de ponto para este usuário?
        </Text>
        {pendingRegisterTimeChange?.value === false && (
          <Textarea
            label="Mensagem de bloqueio"
            description="Mensagem personalizada que será exibida para o usuário (opcional)"
            placeholder="Você não tem permissão para realizar o registro de ponto no momento."
            value={registerTimeBlockMessage}
            onChange={(e) => setRegisterTimeBlockMessage(e.currentTarget.value)}
            mb={16}
          />
        )}
        <Flex justify="end">
          <Button
            variant="default"
            onClick={() => {
              setPageModal(null);
              setRegisterTimeBlockMessage('');
            }}
            mr={8}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (pendingRegisterTimeChange) {
                handleToggleRegisterTime(
                  pendingRegisterTimeChange.id,
                  pendingRegisterTimeChange.value,
                  registerTimeBlockMessage,
                );
              }
            }}
          >
            Confirmar
          </Button>
        </Flex>
      </Modal>
      <Modal
        title="Registrar Entrega de EPI"
        opened={deliverModalRecordId !== null}
        onClose={() => {
          setDeliverModalRecordId(null);
          deliverForm.reset();
        }}
        size="sm"
      >
        <form
          onSubmit={deliverForm.onSubmit((values) => {
            if (deliverModalRecordId === null) return;
            markEpiAsDeliveredFetcher(
              deliverModalRecordId,
              () => {
                setDeliverModalRecordId(null);
                deliverForm.reset();
                listEpiDeliveriesFetcher({
                  employeeId: selectedUserId ?? undefined,
                  limit: 5000,
                });
              },
              {
                reason: values.reason || undefined,
                quantity: values.quantity,
              },
            );
          })}
        >
          <Select
            label="Motivo da entrega"
            placeholder="selecione o motivo"
            clearable
            data={EpiDeliveryReasonOptions}
            mb={12}
            {...deliverForm.getInputProps('reason')}
          />
          <NumberInput
            label="Quantidade"
            min={1}
            mb={20}
            {...deliverForm.getInputProps('quantity')}
          />
          <Group position="right">
            <Button
              variant="outline"
              onClick={() => {
                setDeliverModalRecordId(null);
                deliverForm.reset();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={markEpiDeliverLoading}>
              Confirmar Entrega
            </Button>
          </Group>
        </form>
      </Modal>

      {/* Step 1 — selecionar PDF */}
      <Modal
        title="Importar Holerites em Lote"
        opened={pageModal === 'import-payslip'}
        onClose={handleCloseImportModal}
        closeOnClickOutside={false}
        size="md"
      >
        <Text c="dimmed" size="sm" mb="lg">
          Selecione o PDF geral de holerites. O sistema irá identificar cada
          funcionário pelo nome, separar as páginas e enviar automaticamente por
          e-mail.
        </Text>
        <input
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          id="import-payslip-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handlePreviewBatchPayslip(file);
            e.target.value = '';
          }}
        />
        <Group position="right" mt="md">
          <Button variant="default" onClick={handleCloseImportModal}>
            Cancelar
          </Button>
          <Button
            color="teal"
            leftIcon={<IconFileImport size={16} />}
            onClick={() =>
              document.getElementById('import-payslip-input')?.click()
            }
          >
            Selecionar PDF
          </Button>
        </Group>
      </Modal>

      {/* Step 2 — pré-visualização / revisão */}
      <Modal
        title="Revisão da Importação"
        opened={pageModal === 'import-payslip-preview'}
        onClose={handleCloseImportModal}
        closeOnClickOutside={false}
        size="xl"
      >
        {importBatchPreview && importPreviewFile && (
          <>
            <Flex gap="md" mb="lg" align="center">
              <Text size="sm" fw="bold" c="green">
                ✓ {readyTotal}{' '}
                {readyTotal === 1
                  ? 'funcionário identificado'
                  : 'funcionários identificados'}
              </Text>
              {failedTotal > 0 && (
                <Text size="sm" fw="bold" c="red">
                  ✗ {failedTotal}{' '}
                  {failedTotal === 1
                    ? 'problema encontrado'
                    : 'problemas encontrados'}
                </Text>
              )}
              {payslipFilterIds && (
                <Text size="sm" fw="bold" c="blue">
                  Filtrando {payslipFilterIds.size}{' '}
                  {payslipFilterIds.size === 1
                    ? 'funcionário selecionado'
                    : 'funcionários selecionados'}
                </Text>
              )}
            </Flex>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                maxHeight: 420,
                overflowY: 'auto',
                border: '1px solid #e9ecef',
                borderRadius: 8,
                padding: '4px 0',
              }}
            >
              {(payslipFilterIds
                ? importBatchPreview.pages.filter(
                    (p) => p.employeeId && payslipFilterIds.has(p.employeeId),
                  )
                : importBatchPreview.pages
              ).map((p) => {
                const ok = p.found || !!manualSelections[p.pageIndex];
                return (
                  <div
                    key={p.pageIndex}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: '10px 16px',
                      borderBottom: '1px solid #f1f3f5',
                      background: ok ? '#fff' : '#fff5f5',
                    }}
                  >
                    {/* Thumbnail clicável */}
                    <div
                      style={{
                        flexShrink: 0,
                        border: ok ? '2px solid #12b886' : '2px solid #fa5252',
                        borderRadius: 6,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                      }}
                      title="Clique para ampliar"
                      onClick={() => setExpandedPage(p.pageIndex + 1)}
                    >
                      <PdfPageCanvas
                        file={importPreviewFile}
                        pageNumber={p.pageIndex + 1}
                        scale={0.22}
                      />
                    </div>

                    {/* Informações */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="xs" c="dimmed" mb={2}>
                        Página {p.pageIndex + 1}
                      </Text>
                      <Text
                        size="sm"
                        fw={600}
                        style={{ wordBreak: 'break-word' }}
                      >
                        {p.employeeName ?? (
                          <Text size="sm" c="dimmed" fs="italic">
                            Nome não identificado
                          </Text>
                        )}
                      </Text>
                      {p.found ? (
                        <Text size="xs" c="green" mt={4}>
                          ✓ Funcionário encontrado no sistema
                        </Text>
                      ) : (
                        <>
                          <Text
                            size="xs"
                            c={manualSelections[p.pageIndex] ? 'green' : 'red'}
                            mt={4}
                            mb={6}
                          >
                            {manualSelections[p.pageIndex]
                              ? '✓ Funcionário selecionado manualmente'
                              : `✗ ${p.reason}`}
                          </Text>
                          <Select
                            size="xs"
                            searchable
                            clearable
                            placeholder="Buscar funcionário manualmente..."
                            nothingFound="Digite ao menos 2 letras para buscar"
                            data={manualOptions[p.pageIndex] ?? []}
                            value={
                              manualSelections[p.pageIndex]
                                ? String(manualSelections[p.pageIndex])
                                : null
                            }
                            filter={() => true}
                            onSearchChange={(term) =>
                              handleManualSearch(p.pageIndex, term)
                            }
                            onChange={(value) =>
                              handleManualSelect(p.pageIndex, value)
                            }
                          />
                        </>
                      )}
                    </div>

                    {/* Espelho de ponto por funcionário */}
                    {sendMirrorGlobal && ok && (
                      <Switch
                        size="xs"
                        label={
                          <Text size="xs" color="dimmed">
                            Espelho
                          </Text>
                        }
                        checked={!mirrorExcluded.has(p.pageIndex)}
                        onChange={(e) => {
                          setMirrorExcluded((prev) => {
                            const next = new Set(prev);
                            if (e.currentTarget.checked) {
                              next.delete(p.pageIndex);
                            } else {
                              next.add(p.pageIndex);
                            }
                            return next;
                          });
                        }}
                      />
                    )}

                    {/* Botão ampliar */}
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => setExpandedPage(p.pageIndex + 1)}
                    >
                      Ampliar
                    </Button>
                  </div>
                );
              })}
            </div>

            {failedTotal > 0 && readyTotal > 0 && (
              <Text size="xs" c="dimmed" mt="sm">
                Os holerites com problema serão ignorados. Apenas os{' '}
                {readyTotal} identificados serão enviados.
              </Text>
            )}

            <div
              style={{
                marginTop: 16,
                border: '1px solid #e9ecef',
                borderRadius: 8,
                padding: 12,
              }}
            >
              <Group position="apart" mb={sendMirrorGlobal ? 'sm' : 0}>
                <div>
                  <Text size="sm" fw={500}>
                    Espelho de ponto
                  </Text>
                  <Text size="xs" color="dimmed">
                    Anexar espelho de ponto junto ao email do holerite
                  </Text>
                </div>
                <Switch
                  checked={sendMirrorGlobal}
                  onChange={(e) => {
                    setSendMirrorGlobal(e.currentTarget.checked);
                    if (!e.currentTarget.checked) {
                      setMirrorExcluded(new Set());
                      setImportReferenceMonth(null);
                    }
                  }}
                />
              </Group>

              {sendMirrorGlobal && (
                <>
                  <Select
                    size="sm"
                    label="Mês de referência"
                    placeholder="Selecione o mês"
                    clearable
                    value={importReferenceMonth}
                    onChange={setImportReferenceMonth}
                    data={Array.from({ length: 6 }, (_, i) => {
                      const d = new Date();
                      d.setDate(1);
                      d.setMonth(d.getMonth() - i);
                      const value = `${d.getFullYear()}-${String(
                        d.getMonth() + 1,
                      ).padStart(2, '0')}`;
                      const label = d.toLocaleDateString('pt-BR', {
                        month: 'long',
                        year: 'numeric',
                      });
                      return { value, label };
                    })}
                    mb="sm"
                  />
                  <Text size="xs" color="dimmed">
                    Use o toggle em cada funcionário acima para incluir ou
                    excluir o espelho individualmente.
                  </Text>
                </>
              )}
            </div>

            <Group position="right" mt="lg">
              <Button variant="default" onClick={handleCloseImportModal}>
                Cancelar
              </Button>
              <Button
                color="teal"
                disabled={readyTotal === 0}
                loading={importBatchPayslipLoader}
                leftIcon={<IconFileImport size={16} />}
                onClick={handleConfirmBatchPayslip}
              >
                Confirmar e Enviar ({readyTotal})
              </Button>
            </Group>
          </>
        )}
      </Modal>

      {/* Visualização expandida de uma página */}
      <Modal
        opened={expandedPage !== null}
        onClose={() => setExpandedPage(null)}
        size="xl"
        title={expandedPage !== null ? `Holerite — Página ${expandedPage}` : ''}
        padding="sm"
      >
        {expandedPage !== null && importPreviewFile && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              overflowX: 'auto',
            }}
          >
            <PdfPageCanvas
              file={importPreviewFile}
              pageNumber={expandedPage}
              scale={1.2}
            />
          </div>
        )}
      </Modal>

      {/* Step 3 — resultado */}
      <Modal
        title="Resultado da Importação"
        opened={pageModal === 'import-payslip-result'}
        onClose={handleCloseImportModal}
        size="lg"
      >
        {importBatchResult && (
          <>
            <Flex gap="xl" mb="lg">
              <div>
                <Text size="xl" fw={700} c="green">
                  {importBatchResult.processed.length}
                </Text>
                <Text size="xs" c="dimmed">
                  enviados com sucesso
                </Text>
              </div>
              <div>
                <Text size="xl" fw={700} c="red">
                  {importBatchResult.failed.length}
                </Text>
                <Text size="xs" c="dimmed">
                  com falha
                </Text>
              </div>
            </Flex>

            {importBatchResult.processed.length > 0 && (
              <>
                <Text fw={600} size="sm" mb="xs">
                  Enviados com sucesso
                </Text>
                {importBatchResult.processed.map((name) => (
                  <Text key={name} size="sm" c="green" ml="md">
                    ✓ {name}
                  </Text>
                ))}
              </>
            )}

            {importBatchResult.failed.length > 0 && (
              <>
                <Text fw={600} size="sm" mt="md" mb="xs">
                  Falhas
                </Text>
                {importBatchResult.failed.map((item) => (
                  <Text key={item.name} size="sm" c="red" ml="md">
                    ✗ {item.name} — {item.reason}
                  </Text>
                ))}
              </>
            )}

            <Group position="right" mt="lg">
              <Button onClick={handleCloseImportModal}>Fechar</Button>
            </Group>
          </>
        )}
      </Modal>
    </Page>
  );
}
